import { ServiceStoreConfig } from "../types/ServiceStoreTypes";

/**
 * Dispatch Module Service Stores
 * Services for load management and dispatching in ReadyMix operations
 */
export const dispatchStores: ServiceStoreConfig[] = [
  {
    name: "load",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.loadStore as { entities?: unknown[] };
          const entities = store?.entities || [];
          const totalLoads = entities.length;
          const dispatchedLoads = entities.filter(
            (load: Record<string, unknown>) =>
              load.status === "dispatched" || load.status === "in_transit"
          ).length;
          const deliveredLoads = entities.filter(
            (load: Record<string, unknown>) =>
              load.status === "delivered" || load.status === "completed"
          ).length;
          const pendingLoads = entities.filter(
            (load: Record<string, unknown>) =>
              load.status === "pending" || load.status === "scheduled"
          ).length;

          return [
            {
              title: "Total Loads",
              value: () => totalLoads.toString(),
              valueColor: "primary.main",
              variant: "with-icon" as const,
            },
            {
              title: "Dispatched",
              value: () => dispatchedLoads.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Delivered",
              value: () => deliveredLoads.toString(),
              valueColor: "success.main",
            },
            {
              title: "Pending",
              value: () => pendingLoads.toString(),
              valueColor: "info.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
        defaultSort: {
          sortBy: ["createdAt"],
          sortDirection: { createdAt: "DESC" },
        },
      },
    },
  },
  {
    name: "load-hoist-schedule",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.loadHoistScheduleStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalSchedules = entities.length;
          const todaySchedules = entities.filter(
            (schedule: Record<string, unknown>) => {
              const scheduleDate = new Date(schedule.scheduledDate as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return scheduleDate >= today;
            }
          ).length;
          const activeSchedules = entities.filter(
            (schedule: Record<string, unknown>) =>
              schedule.status === "active" || schedule.status === "in_progress"
          ).length;

          return [
            {
              title: "Total Schedules",
              value: () => totalSchedules.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todaySchedules.toString(),
              valueColor: "info.main",
            },
            {
              title: "Active",
              value: () => activeSchedules.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "trailer-loads",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.trailerLoadsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalLoads = entities.length;
          const loadedTrailers = entities.filter(
            (load: Record<string, unknown>) =>
              load.status === "loaded"
          ).length;
          const inTransitLoads = entities.filter(
            (load: Record<string, unknown>) =>
              load.status === "in_transit"
          ).length;
          const deliveredLoads = entities.filter(
            (load: Record<string, unknown>) =>
              load.status === "delivered"
          ).length;

          return [
            {
              title: "Total Loads",
              value: () => totalLoads.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Loaded",
              value: () => loadedTrailers.toString(),
              valueColor: "info.main",
            },
            {
              title: "In Transit",
              value: () => inTransitLoads.toString(),
              valueColor: "warning.main",
            },
            {
              title: "Delivered",
              value: () => deliveredLoads.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "trailer-configurations",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.trailerConfigurationsStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalConfigs = entities.length;
          const activeConfigs = entities.filter(
            (config: Record<string, unknown>) =>
              config.isActive !== false
          ).length;
          const standardConfigs = entities.filter(
            (config: Record<string, unknown>) =>
              config.type === "standard"
          ).length;
          const customConfigs = entities.filter(
            (config: Record<string, unknown>) =>
              config.type === "custom"
          ).length;

          return [
            {
              title: "Total Configs",
              value: () => totalConfigs.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Active",
              value: () => activeConfigs.toString(),
              valueColor: "success.main",
            },
            {
              title: "Standard",
              value: () => standardConfigs.toString(),
              valueColor: "info.main",
            },
            {
              title: "Custom",
              value: () => customConfigs.toString(),
              valueColor: "warning.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
  {
    name: "load-photos",
    options: {
      enableStorage: true,
      enableErrorNotifications: true,
      idField: "id",
      serviceView: {
        enableServiceView: false,
        summaryCards: (rootStore) => {
          const store = rootStore.loadPhotosStore as {
            entities?: unknown[];
          };
          const entities = store?.entities || [];
          const totalPhotos = entities.length;
          const todayPhotos = entities.filter(
            (photo: Record<string, unknown>) => {
              const photoDate = new Date(photo.createdAt as string);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return photoDate >= today;
            }
          ).length;
          const verifiedPhotos = entities.filter(
            (photo: Record<string, unknown>) =>
              photo.verified === true || photo.status === "verified"
          ).length;

          return [
            {
              title: "Total Photos",
              value: () => totalPhotos.toString(),
              valueColor: "primary.main",
            },
            {
              title: "Today",
              value: () => todayPhotos.toString(),
              valueColor: "info.main",
            },
            {
              title: "Verified",
              value: () => verifiedPhotos.toString(),
              valueColor: "success.main",
            },
          ];
        },
        permissions: {
          requiresAuth: true,
          requiresAdmin: false,
        },
      },
    },
  },
];